import { useEffect, useState, useRef } from 'react'
import { useAuth } from '../auth'
import Layout  from '../components/Layout'
import { logsApi, LogStream, LogEvent, errorMsg } from '../api/client'

const C = { teal: '#2B4C5E', yellow: '#F2B705', muted: '#6B8A91', light: '#C7D9DB' }

function fmtMs(ms: number | null): string {
  if (!ms) return '—'
  return new Date(ms).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
    timeZone: 'Asia/Kolkata',
  })
}

function duration(first: number | null, last: number | null): string {
  if (!first || !last) return '—'
  const s = Math.round((last - first) / 1000)
  if (s < 60) return `${s}s`
  return `${Math.floor(s / 60)}m ${s % 60}s`
}

function statusOf(events: LogEvent[]): 'success' | 'error' | 'running' {
  if (!events.length) return 'running'
  const joined = events.map(e => e.msg).join('\n')
  if (/Error:|Traceback|Exception/.test(joined)) return 'error'
  if (/Done\.|Done$/.test(joined)) return 'success'
  return 'running'
}

const STATUS_STYLE: Record<string, React.CSSProperties> = {
  success: { background: '#dcfce7', color: '#15803d', fontWeight: 600, fontSize: 11, padding: '2px 8px', borderRadius: 4 },
  error:   { background: '#fee2e2', color: '#dc2626', fontWeight: 600, fontSize: 11, padding: '2px 8px', borderRadius: 4 },
  running: { background: '#fef9c3', color: '#854d0e', fontWeight: 600, fontSize: 11, padding: '2px 8px', borderRadius: 4 },
}

function logGroupLabel(lg: string): string {
  if (lg.includes('credit-tat')) return 'Credit TAT Executor'
  if (lg.includes('secure-exec-platform')) return 'Generic Executor'
  return lg.split('/').pop() || lg
}

export default function LogsPage() {
  const { getToken, ready } = useAuth()
  const [logGroups,   setLogGroups]   = useState<string[]>([])
  const [activeGroup, setActiveGroup] = useState<string>('')
  const [streams,     setStreams]     = useState<LogStream[]>([])
  const [loading,     setLoading]     = useState(false)
  const [expanded,    setExpanded]    = useState<string | null>(null)
  const [events,      setEvents]      = useState<LogEvent[]>([])
  const [evtLoad,     setEvtLoad]     = useState(false)
  const [msg,         setMsg]         = useState('')
  const logRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!ready) return
    getToken().then(() =>
      logsApi.groups().then(r => {
        const groups = r.data.log_groups ?? []
        setLogGroups(groups)
        if (groups.length) setActiveGroup(groups[0])
      }).catch(() => {})
    )
  }, [ready])

  useEffect(() => {
    if (activeGroup) load(activeGroup)
  }, [activeGroup])

  async function load(group: string) {
    setLoading(true)
    setExpanded(null)
    setEvents([])
    try {
      await getToken()
      const r = await logsApi.list(group)
      setStreams(r.data.streams ?? [])
    } catch (e: any) {
      setMsg(errorMsg(e, 'Failed to load logs'))
    } finally {
      setLoading(false)
    }
  }

  async function expand(stream: string) {
    if (expanded === stream) { setExpanded(null); setEvents([]); return }
    setExpanded(stream)
    setEvents([])
    setEvtLoad(true)
    try {
      await getToken()
      const r = await logsApi.events(stream, activeGroup)
      setEvents(r.data.events ?? [])
      setTimeout(() => logRef.current?.scrollTo(0, logRef.current.scrollHeight), 50)
    } catch (e: any) {
      setMsg(errorMsg(e, 'Failed to load events'))
    } finally {
      setEvtLoad(false)
    }
  }

  return (
    <Layout>
      <main style={{ padding: '24px 20px', minHeight: 'calc(100vh - 64px)' }}>

        {msg && (
          <div style={{
            position: 'fixed', top: 80, right: 24,
            background: '#7f1d1d', color: '#fff',
            padding: '12px 20px', borderRadius: 8,
            borderLeft: '4px solid #ef4444', zIndex: 2000, maxWidth: 420,
          }}>{msg}</div>
        )}

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
          <div>
            <h1 style={{ color: C.teal, fontSize: 24, fontWeight: 700 }}>Execution Logs</h1>
            <p style={{ color: C.muted, fontSize: 14, marginTop: 4 }}>
              CloudWatch log streams — most recent executions first
            </p>
          </div>
          <button onClick={() => load(activeGroup)} disabled={loading} style={{
            background: C.teal, color: '#fff', border: 'none',
            padding: '8px 18px', borderRadius: 6, fontSize: 13,
            fontWeight: 600, cursor: 'pointer',
          }}>
            {loading ? '…' : '↻ Refresh'}
          </button>
        </div>

        {/* Log group selector */}
        {logGroups.length > 1 && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
            {logGroups.map(lg => (
              <button key={lg} onClick={() => setActiveGroup(lg)} style={{
                padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontSize: 13,
                border: `1.5px solid ${activeGroup === lg ? C.yellow : '#d1d5db'}`,
                background: activeGroup === lg ? 'rgba(242,183,5,0.08)' : '#fff',
                color:  activeGroup === lg ? C.teal : C.muted,
                fontWeight: activeGroup === lg ? 600 : 400,
              }}>
                {logGroupLabel(lg)}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: C.muted }}>Loading streams…</div>
        ) : streams.length === 0 ? (
          <div style={{
            background: '#fff', borderRadius: 12, padding: '60px 40px',
            textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🗒️</div>
            <div style={{ color: C.teal, fontWeight: 600, fontSize: 18 }}>No executions yet</div>
            <div style={{ color: C.muted, fontSize: 14, marginTop: 8 }}>
              Logs appear here after a report runs
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {streams.map(s => (
              <StreamRow
                key={s.stream}
                stream={s}
                isOpen={expanded === s.stream}
                events={expanded === s.stream ? events : []}
                evtLoad={evtLoad && expanded === s.stream}
                logRef={expanded === s.stream ? logRef : undefined}
                onToggle={() => expand(s.stream)}
              />
            ))}
          </div>
        )}
      </main>
    </Layout>
  )
}

function StreamRow({ stream, isOpen, events, evtLoad, logRef, onToggle }: {
  stream:   LogStream
  isOpen:   boolean
  events:   LogEvent[]
  evtLoad:  boolean
  logRef?:  React.RefObject<HTMLDivElement>
  onToggle: () => void
}) {
  const status     = isOpen ? statusOf(events) : guessStatus(stream)
  const taskShort  = stream.task_id.slice(0, 12)

  return (
    <div style={{
      background: '#fff', borderRadius: 10,
      boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
      border: `1px solid ${isOpen ? C.yellow : 'transparent'}`,
      overflow: 'hidden',
    }}>
      <div onClick={onToggle} style={{
        display: 'flex', alignItems: 'center', gap: 16,
        padding: '14px 20px', cursor: 'pointer', userSelect: 'none',
      }}>
        <span style={{ color: C.muted, fontSize: 18 }}>{isOpen ? '▾' : '▸'}</span>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'monospace', fontSize: 13, color: C.teal, fontWeight: 600 }}>
              {taskShort}…
            </span>
            <span style={STATUS_STYLE[status]}>{status}</span>
          </div>
          <div style={{ color: C.muted, fontSize: 12, marginTop: 4 }}>
            {fmtMs(stream.first_ms)}
            {stream.first_ms && stream.last_ms
              ? <span style={{ marginLeft: 12 }}>⏱ {duration(stream.first_ms, stream.last_ms)}</span>
              : null}
          </div>
        </div>

        <div style={{ color: C.muted, fontSize: 11, whiteSpace: 'nowrap' }}>
          {stream.size ? `${(stream.size / 1024).toFixed(1)} KB` : ''}
        </div>
      </div>

      {isOpen && (
        <div style={{ borderTop: '1px solid #f3f4f6' }}>
          {evtLoad ? (
            <div style={{ padding: '16px 24px', color: C.muted, fontSize: 13 }}>Loading…</div>
          ) : (
            <div
              ref={logRef}
              style={{
                fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6,
                background: '#0f1f26', color: '#c7d9db',
                padding: '16px 20px', maxHeight: 420, overflowY: 'auto',
              }}
            >
              {events.length === 0
                ? <span style={{ color: '#6B8A91' }}>No events</span>
                : events.map((e, i) => <LogLine key={i} event={e} />)
              }
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function LogLine({ event }: { event: LogEvent }) {
  const time = new Date(event.ts).toLocaleTimeString('en-IN', {
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false, timeZone: 'Asia/Kolkata',
  })
  const msg = event.msg.trimEnd()
  let color = '#c7d9db'
  if (/Error:|Traceback|Exception/.test(msg)) color = '#fca5a5'
  else if (/Done\.|success|uploaded|sent/i.test(msg)) color = '#86efac'
  else if (/WARNING|warn/i.test(msg)) color = '#fde68a'
  else if (/Downloading|Running|Processing/i.test(msg)) color = '#93c5fd'

  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 2 }}>
      <span style={{ color: '#4a7080', flexShrink: 0 }}>{time}</span>
      <span style={{ color, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{msg}</span>
    </div>
  )
}

function guessStatus(s: LogStream): 'success' | 'error' | 'running' {
  if (!s.first_ms) return 'running'
  const age = Date.now() - (s.last_ms ?? s.first_ms)
  if (age < 5 * 60 * 1000) return 'running'
  return 'success'
}
